// src/BookingDetailDialog.js - the whole booking, and what an admin may change
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, MenuItem, Divider, Alert, CircularProgress,
} from '@mui/material';
import { supabase } from './supabaseClient';
import BookingLedger from './BookingLedger';
import BookingTracker from './BookingTracker';
import BookingAllowance from './BookingAllowance';
import BookingMoneyActions from './BookingMoneyActions';
import { ink } from './designTokens';

import { prettyStatus } from './labels';
// What an admin may set a booking to, and nothing else.
//
// Deliberately narrow. Price, dates and the stylist are not here: each of them
// is mirrored somewhere this console does not control — Stripe holds the money
// against the original amount, and a calendar event carries the time — so
// changing one here would leave two systems disagreeing, with the console
// being the one that is wrong.
const SETTABLE = ['Requested', 'Confirmed', 'Completed', 'Declined', 'Cancelled'];

const Field = ({ label, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" sx={{ color: ink[500], display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {children ?? '—'}
    </Typography>
  </Box>
);

export default function BookingDetailDialog({ booking, budget, open, onClose, onSaved }) {
  const [status, setStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!booking) return;
    setStatus(booking.status || '');
    setAdminNotes(booking.admin_notes || '');
    setMessage(null);
  }, [booking]);

  if (!booking) return null;

  const changed = status !== booking.status || adminNotes !== (booking.admin_notes || '');

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          status,
          admin_notes: adminNotes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', booking.id);
      if (error) throw error;

      // Who changed what, recorded server-side. A status moved by hand is
      // exactly the kind of thing someone asks about a month later, and
      // "the console says Completed" is not an answer without this.
      try {
        await supabase.rpc('write_admin_audit', {
          p_action: 'booking_updated',
          p_entity_type: 'booking',
          p_entity_id: booking.id,
          p_payload: {
            from_status: booking.status,
            to_status: status,
            notes_changed: adminNotes !== (booking.admin_notes || ''),
          },
        });
      } catch (auditErr) {
        // Never let the log stop the change it is describing.
        console.warn('Could not write the audit entry:', auditErr);
      }

      setMessage({ severity: 'success', text: 'Saved.' });
      onSaved?.();
    } catch (err) {
      setMessage({ severity: 'error', text: `Could not save: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const when = booking.start_ts ? new Date(booking.start_ts) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Booking #{booking.id?.slice(0, 8).toUpperCase()}
      </DialogTitle>
      <DialogContent dividers>
        <BookingTracker status={booking.status} />

        <Box sx={{ my: 2 }}>
          <BookingAllowance budget={budget} />
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
          <Field label="Service">{booking.service_title}</Field>
          <Field label="When">
            {when ? when.toLocaleString() : null}
          </Field>
          <Field label="Client">{booking.phone_number}</Field>
          <Field label="Stylist">
            {booking.stylists_phone_number || booking.stylist_phone_number}
          </Field>
          <Field label="Price">
            {booking.price != null
              ? `$${parseFloat(booking.price).toFixed(2)} ${booking.currency || ''}`.trim()
              : null}
          </Field>
          <Field label="Payment">
            {booking.payment_completed ? 'Completed' : 'Not completed'}
          </Field>
          <Field label="How they meet">{booking.communication_method}</Field>
          <Field label="Requested">
            {booking.created_at ? new Date(booking.created_at).toLocaleString() : null}
          </Field>
          <Field label="Approved">
            {booking.approved_at ? new Date(booking.approved_at).toLocaleString() : 'Not approved'}
          </Field>
          <Field label="Booking id">{booking.id}</Field>
        </Box>

        {booking.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            {/* The client's own note. Read-only here: it is theirs, and the
                stylist sees the same text. */}
            <Field label="Note from the client">{booking.notes}</Field>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <BookingMoneyActions
          booking={booking}
          budget={budget}
          onDone={onSaved}
        />

        <BookingLedger booking={booking} />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1.5 }}>
          Status and notes
        </Typography>

        <TextField
          select
          fullWidth
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ mb: 2 }}
          helperText={
            status !== booking.status
              ? `Changing from "${prettyStatus(booking.status)}". This does not move any money — Stripe is unaffected.`
              : 'Price, date and stylist are not editable here: Stripe and the calendar hold their own copies, and this console is not the one that would be right.'
          }
        >
          {SETTABLE.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
          {/* Whatever it currently is, even if it is not a value an admin may
              set — otherwise the field would silently show the wrong status. */}
          {!SETTABLE.includes(booking.status) && booking.status && (
            <MenuItem value={booking.status} disabled>
              {booking.status} (current)
            </MenuItem>
          )}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label="Internal note"
          placeholder="Why this was changed. Only admins see this."
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
        />

        {message && (
          <Alert severity={message.severity} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!changed || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
